import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { DefaultGlobalErrorView } from './components/default-global-error-view';

const instrumentationSchema = {
  scenario: [{
    name: 'appReactGlobalError',
    value: 'Log application global react error',
    activity: [
      {
        name: 'logError',
        value: 'Log Error Info',
      },
    ],
  }],
};

function createErrorMessage(error, { componentStack = '' }) {
  if (_.isString(error)) {
    return `${error} ${componentStack}`;
  }
  if (_.isObject(error)) {
    const errorStack = error.stack ? error.stack : '';
    return `${error.message} ${componentStack} ${errorStack}`.trim();
  }
  // If some other object, log it as is
  try {
    return error.toString();
  } catch (e) {
    return 'Error could not be serialized';
  }
}

function getErrorView() {
  return () => <DefaultGlobalErrorView />;
}

class ComponentError extends Error {
  constructor(...params) {
    super(...params);
    this.name = 'ComponentError';
  }
}

export const getGlobalErrorOptions = ({ instrumentation, componentName, errorAction = _.noop }) => ({
  logError: ({ error, info }) => {
    if (error instanceof ComponentError) {
      // the error is logged in component error boundary, do not log error again.
      return;
    }

    let logError;
    if (componentName) {
      const scenario = instrumentation.createScenario({
        scenarioName: `${componentName}ErrorBoundary`,
      });
      logError = scenario.createActivity('logError');
    } else {
      instrumentation.addScenario(instrumentationSchema);
      const appReactGlobalError = instrumentation.appReactGlobalError.create();
      logError = appReactGlobalError.logError.create();
    }

    logError.error(createErrorMessage(error, info));
    if (componentName) {
      // if this is a component error boundary, rethrow error so that parent error boundary can handle error view.
      throw new ComponentError(error);
    }
    setClarityTag({ hasGlobalError: 'true' });

    errorAction(error);
  },
  renderErrorView: getErrorView(),
});

export const withGlobalErrorOptions = (Component, componentName, errorAction) => {
  const Wrapper = React.memo(({ instrumentation, ...props }) => (
    <Component globalErrorOptions={getGlobalErrorOptions({ instrumentation, componentName, errorAction })} {...props} />
  ));

  Wrapper.propTypes = {
    instrumentation: PropTypes.shape({
      addScenario: PropTypes.func.isRequired,
    }).isRequired,
  };

  return withInstrumentation(Wrapper);
};
